/**
 * LetzteAktivitaeten — the last-8 activity feed (Story UI-U7, AC 6).
 *
 * The DashboardScreen activity pattern: an avatar square, "<actor> <content>", a
 * subject/task ref chip + a short relative time. Fed by the broker-visible `activity`
 * timeline (letzte_aktivitaeten) — NOT the WORM audit_log. actor_name falls back to
 * "Brokly" for system/ai actors.
 *
 * [Source: apps/web/src/features/dashboard/DashboardScreen.tsx (activity card)]
 * [Source: docs/stories/ui-u7-portfolio.md AC 6]
 */

import type { PortfolioAktivitaetDto } from '@brokly/shared';
import { Card } from '../../../components/ui/Card';
import { relTimeDe } from '../util';

/** First letters for the avatar square. System/AI actors → 'BR' (Brokly). */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'BR';
  return parts
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
}

export function LetzteAktivitaeten({ items }: { items: PortfolioAktivitaetDto[] }) {
  return (
    <Card className="rounded-xl p-4">
      <div className="mb-3 text-sm font-medium">Letzte Aktivitäten</div>
      {items.length === 0 ? (
        <div className="grid h-32 place-items-center text-sm text-muted-foreground">
          Noch keine Aktivitäten.
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((a) => {
            const actor = a.actor_name ?? 'Brokly';
            const ref = a.subject_name ?? (a.task_id ? 'Aufgabe' : null);
            return (
              <li key={a.id} className="flex items-start gap-3">
                <div className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-md bg-secondary text-[11px] font-medium text-muted-foreground">
                  {initials(actor)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] leading-snug text-foreground">
                    <span className="font-medium">{actor}</span>{' '}
                    <span className="text-foreground/80">{a.content ?? '-'}</span>
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                    {ref && (
                      <span className="rounded border border-border px-1.5 py-0.5">{ref}</span>
                    )}
                    <span>{relTimeDe(a.created_at)}</span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
